/**
 * IndexedDB-backed offline write queue.
 * Any POST we'd like to survive a connectivity blip (daily streak logs,
 * journal entries, dream logs, symptom logs) gets pushed here when the
 * browser is offline and auto-replays on reconnect.
 *
 * Schema: db 'lalaland-offline' object store 'queue' with keyPath 'id'.
 * Records: { id, url, method, body, headers, meta, createdAt }
 */

const DB_NAME = "lalaland-offline";
const DB_VERSION = 1;
const STORE = "queue";

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "id", autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function withStore(mode, fn) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const store = tx.objectStore(STORE);
    const result = fn(store);
    tx.oncomplete = () => resolve(result);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

/** Queue a request. Returns the generated id. */
export async function enqueue(entry) {
  await withStore("readwrite", (store) => {
    store.add({
      url: entry.url,
      method: entry.method || "POST",
      body: entry.body ?? null,
      headers: entry.headers || {},
      meta: entry.meta || {},
      createdAt: new Date().toISOString(),
    });
  });
  notifyListeners();
}

/** Return [{ id, url, ... }] oldest first. */
export function getAll() {
  return new Promise(async (resolve, reject) => {
    const db = await openDb();
    const tx = db.transaction(STORE, "readonly");
    const req = tx.objectStore(STORE).getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => reject(req.error);
  });
}

export async function remove(id) {
  await withStore("readwrite", (store) => store.delete(id));
  notifyListeners();
}

export async function size() {
  const all = await getAll();
  return all.length;
}

// --- listeners (so UI can show queue size) ---
const listeners = new Set();
export function subscribe(fn) {
  listeners.add(fn);
  // Fire once so callers get current state
  size().then((n) => fn(n));
  return () => listeners.delete(fn);
}
function notifyListeners() {
  size().then((n) => listeners.forEach((fn) => fn(n)));
}

/**
 * Flush all queued requests in insertion order. Stops at the first
 * network failure to preserve ordering (a user's vibe-check should always
 * sync before the symptom log they wrote afterwards).
 * Returns { flushed, failed }.
 */
export async function flush() {
  if (!navigator.onLine) return { flushed: 0, failed: 0, skipped: true };
  const all = await getAll();
  let flushed = 0;
  let failed = 0;
  for (const entry of all) {
    try {
      const res = await fetch(entry.url, {
        method: entry.method,
        headers: {
          "Content-Type": "application/json",
          ...entry.headers,
        },
        body: entry.body ? JSON.stringify(entry.body) : undefined,
      });
      if (res.ok || res.status === 409) {
        // 409 = conflict (already logged today) — treat as success so we don't
        // retry forever when the backend dedupes a replayed daily log.
        await remove(entry.id);
        flushed += 1;
      } else {
        failed += 1;
        break; // stop — preserve order for the next attempt
      }
    } catch (_err) {
      failed += 1;
      break;
    }
  }
  return { flushed, failed };
}

/**
 * Lifecycle: auto-flush whenever the browser says we're back online.
 * Safe to call more than once — we guard with a module-level flag.
 */
let initialized = false;
export function initOfflineQueue() {
  if (initialized || typeof window === "undefined") return;
  initialized = true;
  window.addEventListener("online", () => {
    flush().catch((err) => console.warn("[offline-queue] flush failed:", err));
  });
  // Also try once on load in case we crashed mid-flush last session
  if (navigator.onLine) {
    setTimeout(() => flush().catch(() => {}), 1500);
  }
}
